import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useSearchParams, Navigate } from "react-router-dom";
import { allAssetRequestsData } from "./mockData"; // Optional: keep for fallback
import { getMyAssets, getIncomingAssets, postAssetaccept, postAssetReject } from "../../ApiHelper/url_Helpers";
import CommonTable from "../../Components/CommonTable";
import { FaClock, FaExchangeAlt, FaCheckCircle, FaTimesCircle, FaCheck, FaTimes, FaArrowLeft } from "react-icons/fa";
import { Search } from "lucide-react";
import FilterDropdown from "../Approvals/ApprovalFilter";
import Loader from "../../Components/Loader";
import { toasterError, toasterSuccess } from "../../Components/toaster";

const myRequestsStatusOptions = [
  { label: "Waiting for Receiver", key: "waiting_for_receiver" },
  { label: "Waiting for HR", key: "waiting_for_hr" },
  { label: "Transferred", key: "transferred" },
  { label: "Returned", key: "returned" },
  { label: "Rejected", key: "rejected" }
];

const incomingStatusOptions = [
  { label: "Waiting for You", key: "waiting_for_you" },
  { label: "Waiting for HR", key: "waiting_for_hr" },
  { label: "Transferred", key: "transferred" },
  { label: "Rejected", key: "rejected" }
];


const sortRequestAsset = [{ label: "Return", key: "return" }, { label: "Transfer", key: "transfer" },]



const AssetRequests = () => {
  const navigate = useNavigate();
  const { tab } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialPage = parseInt(searchParams.get("page") || "1", 10);
  const initialSearch = searchParams.get("search") || "";
  const initialStatus = searchParams.get("status") || "";

  const [page, setPage] = useState(initialPage);
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(initialSearch);
  const [filters, setFilters] = useState({ status: initialStatus, request_type: searchParams.get("request_type") || "" });
  const [requestsData, setRequestsData] = useState([]);
  const [paginationData, setPaginationData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchRequests = async (currentPage = page) => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        page_size: 10,
        search: debouncedSearchQuery,
        status: filters.status,
        request_type: filters.request_type,
      };

      const filteredParams = Object.fromEntries(
        Object.entries(params).filter(([_, v]) => v !== "" && v !== null && v !== undefined)
      );

      const res = tab === "myAsset"
        ? await getMyAssets(filteredParams)
        : await getIncomingAssets(filteredParams);

      setRequestsData(res?.data?.results || res?.data?.requests || res?.data || []);

      if (res?.data?.pagination) {
        setPaginationData(res.data.pagination);
      } else if (res?.data?.count !== undefined) {
        setPaginationData({
          current_page: currentPage,
          total_records: res.data.count,
          next: res.data.next,
          previous: res.data.previous
        });
      }
    } catch (error) {
      console.error("Error fetching asset requests:", error);
    } finally {
      setLoading(false);
    }
  };

  const isInitialMount = useRef(true);
  const prevTab = useRef(tab);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      if (searchQuery !== initialSearch) {
        setPage(1);
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (prevTab.current !== tab) {
      setSearchQuery("");
      setDebouncedSearchQuery("");
      setFilters({ status: "", request_type: "" });
      prevTab.current = tab;
      setPage(1);
    }
  }, [tab]);

  useEffect(() => {
    fetchRequests(page);
  }, [page, tab, debouncedSearchQuery, filters.status, filters.request_type]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (page > 1) params.set("page", page.toString());
    if (debouncedSearchQuery) params.set("search", debouncedSearchQuery);
    if (filters.status) params.set("status", filters.status);
    if (filters.request_type) params.set("request_type", filters.request_type);

    setSearchParams(params, { replace: true });
  }, [page, debouncedSearchQuery, filters, setSearchParams]);

  if (tab !== "myAsset" && tab !== "incoming") {
    return <Navigate to="../myAsset" replace />;
  }

  const handleAction = async (requestId, actionType) => {
    try {
      if (actionType === "accept") {
        await postAssetaccept(requestId);
        toasterSuccess("Asset accepted successfully");
      } else {
        await postAssetReject(requestId);
        toasterSuccess("Asset rejected successfully");
      }
      fetchRequests(); // Refresh the list
    } catch (error) {
      console.error(`Failed to ${actionType} asset:`, error);
      toasterError(`Failed to ${actionType} asset`);
    }
  };

  const renderStatus = (status) => {
    let bg = "bg-gray-100", text = "text-gray-700";
    if (status === "Pending HR Approval" || status?.toLowerCase().includes("waiting") || status?.toLowerCase().includes("pending")) { bg = "bg-orange-100"; text = "text-orange-600"; }
    else if (status === "Waiting for Receiver") { bg = "bg-blue-100"; text = "text-blue-600"; }
    else if (status === "Rejected" || status === "rejected") { bg = "bg-red-100"; text = "text-red-600"; }
    else if (status === "completed" || status === "approved" || status === "transferred" ||
      status === "Completed" || status === "Approved" || status === "Transferred" || status === "Returned" || status === "returned"
    ) { bg = "bg-green-100"; text = "text-green-600"; }

    return (
      <span className={`px-3 py-1 text-sm font-medium rounded-md ${bg} ${text} whitespace-nowrap`}>
        {status}
      </span>
    );
  };

  const columns = [
    { key: "request_id", label: "Request ID" },
    {
      key: "asset",
      label: "Asset",
      render: (v) => (
        <div className="flex flex-col">
          <span className="text-xs text-gray-500"> {v.asset_name || v.name}</span>
          <span className="font-medium text-sm">{v.asset_type}</span>
        </div>
      )
    },
    {
      key: "recipient",
      label: "Employee (From)",
      render: (_, row) => {
        const employee = row.recipient || row.from_employee;

        return (
          <div className="flex flex-col">
            <span className="font-medium text-sm">{employee?.name}</span>
            <span className="text-xs text-gray-500">{employee?.employee_code}</span>
          </div>
        );
      },
    },
    {
      key: "request_type_display",
      label: "Type",
      render: (v) => <span className="px-3 py-1 text-xs font-medium bg-blue-50 text-blue-500 rounded-md border border-blue-100">{v || "Incoming"}</span>
    },
    {
      key: "requested_at",
      label: "Request Date",
      render: (value) =>
        value ? new Date(value).toLocaleDateString("en-GB") : "-",
    },
    { key: "status_display", label: "Status", render: renderStatus },
    {
      key: "actions",
      label: "Actions",
      render: (_, row) => {
        if (row.request_type_display !== "Transfer" && row.status_display === "Waiting for You") {

          return (
            <div className="flex gap-2">
              <button
                onClick={() => handleAction(row.id, "accept")}
                className="p-1.5 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors"
              >
                <FaCheck />
              </button>
              <button
                onClick={() => handleAction(row.id, "reject")}
                className="p-1.5 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
              >
                <FaTimes />
              </button>
            </div>
          );
        } else if (row.status_display === "Waiting for HR" || row.status_display === "Waiting for Receiver") {
          return <span className="text-orange-400  font-medium text-sm bg-orange-50 px-2 py-1 whitespace-nowrap rounded-md">{row?.status_display}</span>;
        } else if (row.status_display === "Completed" || row.status_display === "Approved" || row.status_display === "Transferred" || row.status_display === "Returned" || row.status_display === "approved" || row.status_display === "transferred") {
          return <span className="text-green-500 font-medium text-sm bg-green-50 px-2 py-1 rounded-md whitespace-nowrap">{row?.status_display || "Completed"}</span>;
        } else if (row.status_display === "Rejected" || row.status_display === "rejected") {
          return <span className="text-red-500 font-medium text-sm bg-red-50 px-2 py-1 rounded-md whitespace-nowrap">{row?.status_display || "Rejected"}</span>;
        }
        return <span className="text-light-500 font-medium text-sm bg-light-50 px-2 py-1 rounded-md whitespace-nowrap">{row?.status_display || ""}</span>;
      }
    }
  ];

  return (
    <div className="px-4 md:p-8 bg-[#f8fafc] min-h-screen">
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <Loader />
        </div>
      ) : (
        <div>
          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <button onClick={() => navigate("/company-assets")} className="text-gray-500 hover:text-black transition-colors cursor-pointer p-1">
              <FaArrowLeft className="text-xl" />
            </button>
            <div>
              <h2 className="text-xl font-bold text-gray-800">Asset Requests</h2>
              <p className="text-sm text-gray-500">Manage and process asset transfer and return requests</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200 mb-6">
            <button
              className={`py-2 px-4 font-medium text-sm transition-colors ${tab === "myAsset"
                ? "border-b-2 border-blue-500 text-blue-600"
                : "text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              onClick={() => { navigate("../requests/myAsset"); }}
            >
              My Requests
            </button>
            <button
              className={`py-2 px-4 font-medium text-sm transition-colors ${tab === "incoming"
                ? "border-b-2 border-blue-500 text-blue-600"
                : "text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              onClick={() => navigate("../requests/incoming")}
            >
              Incoming Requests
            </button>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-4 justify-between items-center">
            <div className="relative flex-grow sm:max-w-xs">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <input
                value={searchQuery}
                onChange={(e) => {setSearchQuery(e.target.value); setPage(1);}}
                placeholder="Search..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-300"
              />
            </div>
            <div className="flex gap-2 md:gap-4 items-center">
                <FilterDropdown
                  list={tab === "myAsset" ? myRequestsStatusOptions : incomingStatusOptions}
                  title="Status"
                  keyName="status"
                  name="status"
                  value={filters}
                  onChange={(n) => { setFilters((s) => ({ ...s, ...n })); setPage(1); }}
                  onClear={() => { setFilters((s) => ({ ...s, status: "" })); setPage(1); }}
                />
              {tab === "myAsset" &&
                <FilterDropdown
                  list={sortRequestAsset}
                  title="Request Type"
                  keyName="request_type"
                  name="Request Type"
                  value={filters}
                  onChange={(n) => { setFilters((s) => ({ ...s, ...n })); setPage(1); }}
                  onClear={() => { setFilters((s) => ({ ...s, request_type: "" })); setPage(1); }}
                />
              }

              <button
                onClick={() => {
                  setFilters({ status: "", request_type: "" });
                  setSearchQuery("");
                  setPage(1);
                }}
                className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Reset
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <CommonTable
              data={Array.isArray(requestsData) ? requestsData : (requestsData?.requests || [])}
              rowKey="request_id"
              columns={columns}
              page={paginationData?.current_page || page}
              onPageChange={(url) => {
                if (url) {
                  try {
                    const urlObj = new URL(url);
                    const newPage = urlObj.searchParams.get("page");
                    setPage(newPage ? Number(newPage) : 1);
                  } catch (e) {
                    console.error("Invalid pagination URL:", url);
                  }
                }
              }}
              nextPageUrl={paginationData?.next}
              prevPageUrl={paginationData?.previous}
              totalCount={paginationData?.total_records || (Array.isArray(requestsData) ? requestsData.length : (requestsData?.total_requests || 0))}
            />
          </div>

        </div>
      )}
    </div>
  );
};

export default AssetRequests;
